import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import FileInfo from "./OrderTrackingFileInfo";
import {
  ScorecardPreviewModal,
  WalkthroughPreviewModal,
  ImagePreviewModal,
} from "./PreviewModals";
import {
  Attachment,
  hasFile,
  isImage,
  isMedia,
  isScorecard,
  isUploading,
  isValid,
  isWalkthrough,
  ValidImage,
} from "@utils/useManageAttachments";
import { Dayjs } from "dayjs";

// TODO: ImagePreviewModal can change to just accept the type-narrowed
// Attachment object itself, once comments have been adapted to use the
// Attachment type too.
type ImageDetails = {
  title: string;
  alt: string;
  size: number;
  createdAt?: Dayjs;
};

const getImageAttachmentDetails = (att: ValidImage): ImageDetails => {
  return {
    title: att.name,
    alt: att.name,
    size: att.size,
    createdAt: att.state !== "pending" ? att.createdAt : undefined,
  };
};

interface Props {
  attachmentEntries: [string, Attachment][];
  disabled: boolean;
  isEditMode: boolean;
  deleteAttachment: (key: string, att: Attachment) => void;
}

const OrderTrackingFileList = ({
  attachmentEntries,
  isEditMode,
  disabled,
  deleteAttachment,
}: Props) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => URL.revokeObjectURL(previewUrl ?? "");
  }, [previewUrl, previewOpen]);

  const handleOpenPreview = (attachment: Attachment) => {
    setPreviewAttachment(attachment);
    if (hasFile(attachment)) {
      setPreviewUrl(URL.createObjectURL(attachment.file));
    } else if (isValid(attachment) && isMedia(attachment)) {
      setPreviewUrl(attachment.url);
    }
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  return (
    <Stack>
      <Stack
        sx={{
          paddingX: 2,
          paddingTop: 1,
        }}
      >
        <Stack>
          {/* Don't show unsent media that is marked for deletion */}
          {attachmentEntries
            .filter(([_, att]) => {
              if (isUploading(att) && att.toDelete) {
                return false;
              }
              return true;
            })
            .map(([key, attachment]) => (
              <FileInfo
                key={key}
                attachmentKey={key}
                attachment={attachment}
                onDelete={deleteAttachment}
                openPreview={handleOpenPreview}
                isEditMode={isEditMode}
                disabled={disabled}
              />
            ))}
        </Stack>
      </Stack>
      {previewOpen && previewAttachment && (
        <>
          {isScorecard(previewAttachment) && isValid(previewAttachment) && (
            <ScorecardPreviewModal
              attachment={previewAttachment}
              onClose={handleClosePreview}
            />
          )}
          {isWalkthrough(previewAttachment) && isValid(previewAttachment) && (
            <WalkthroughPreviewModal
              attachment={previewAttachment}
              onClose={handleClosePreview}
            />
          )}
          {isImage(previewAttachment) && isValid(previewAttachment) && (
            <ImagePreviewModal
              open={previewOpen}
              onClose={handleClosePreview}
              src={previewUrl ?? ""}
              {...getImageAttachmentDetails(previewAttachment)}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default OrderTrackingFileList;
